import { datadogRum } from '@wehunt/core-web'
import { ApiError } from '../../shared/api'
import { isRecipientRejected } from '../teams/inviteErrors'
import type { MemberInvite } from '../teams/inviteInput'
import type { HuntGuestInviteStatus } from './useInviteHuntGuest'

/** Where on web the invite was sent from. */
export type HuntGuestInviteSource = 'hunt_detail' | 'plan_wizard'

/** Whether the hunt had started when the invite went out. */
export type HuntGuestInviteHuntState = 'planned' | 'ongoing'

/**
 * Why an invite failed, in the same words mobile's `HuntGuestInvited`
 * `failureReason` uses, so the two reports line up.
 */
export type HuntGuestInviteFailureReason =
  | 'not_allowed'
  | 'invalid_address'
  | 'delivery_not_configured'
  | 'network'
  | 'unexpected'

export function huntGuestInviteFailureReason(e: unknown): HuntGuestInviteFailureReason {
  if (!(e instanceof ApiError)) return 'network'
  if (e.status === 403) return 'not_allowed'
  if (isRecipientRejected(e)) return 'invalid_address'
  // A 422 for a plan with no join code is a server-side gap, not a bad
  // address; mobile files it as unexpected too.
  if (e.status === 422 && e.problem.code === 'hunt_guest_invite.no_join_code') return 'unexpected'
  if (e.status === 422) return 'invalid_address'
  if (e.status === 503) return 'delivery_not_configured'
  return 'unexpected'
}

/** Planned when the hunt starts later than `now`, ongoing otherwise. */
export function huntGuestInviteHuntState(startTime: string | null | undefined, now = new Date()): HuntGuestInviteHuntState {
  if (!startTime) return 'planned'
  const start = new Date(startTime)
  return Number.isNaN(start.getTime()) || start > now ? 'planned' : 'ongoing'
}

/**
 * Reports one guest invitation as the `hunt_guest_invited` RUM action, the web
 * counterpart of mobile's `HuntGuestInvited` Mixpanel event: `channel` is what
 * the organiser typed, `result` what the server answered (`invited_email`,
 * `invited_sms`, `invited_in_app`) or `failed` with a `failureReason`.
 *
 * Carries no address and no id. RUM already holds the view URL, and an id here
 * would add nothing a hashed one on mobile does not.
 */
export function trackHuntGuestInvited(args: {
  channel: MemberInvite['channel']
  outcome: { status: HuntGuestInviteStatus } | { error: unknown }
  source: HuntGuestInviteSource
  huntState: HuntGuestInviteHuntState
}) {
  const { channel, outcome, source, huntState } = args
  datadogRum.addAction('hunt_guest_invited', {
    channel,
    result: 'status' in outcome ? outcome.status : 'failed',
    ...('error' in outcome ? { failureReason: huntGuestInviteFailureReason(outcome.error) } : {}),
    source,
    huntState,
  })
}
